import { createSceneActionFromFlow } from '../../../converters/helpers';
import type { FlowCardDefinition } from '../types';

const actionOptions = [
	{ value: 'play', label: 'Run' },
	{ value: 'enable', label: 'Enable' },
	{ value: 'disable', label: 'Disable' },
	{ value: 'toggle', label: 'Toggle enabled state' },
];

const playOptions = [
	{ value: 'with_if_evaluation', label: 'Check its conditions' },
	{ value: 'without_if_evaluation', label: 'Run immediately' },
];

function targetName(data: Record<string, any>, alarm: boolean): string {
	return (
		String(data.sceneName || '').trim() ||
		`${alarm ? 'Alarm' : 'Scene'} ${data.sceneId || ''}`.trim()
	);
}

function chooseTarget(alarm: boolean): string {
	return `Choose ${alarm ? 'an alarm' : 'a scene'}`;
}

function actionSummary(data: Record<string, any>, alarm: boolean): string {
	if (!data.sceneId) return chooseTarget(alarm);
	const verbs: Record<string, string> = {
		play: alarm ? 'Trigger' : 'Run',
		enable: 'Enable',
		disable: 'Disable',
		toggle: 'Toggle',
	};
	return `${verbs[data.actionType] || 'Run'} ${targetName(data, alarm)}`;
}

function normalizeSceneAction(data: Record<string, any>, alarm: boolean): Record<string, any> {
	return {
		...data,
		filterType: alarm ? 'alarm' : 'scene',
		label: actionSummary(data, alarm),
	};
}

function sceneActionCard(alarm: boolean): FlowCardDefinition {
	const type = alarm ? 'alarmAction' : 'sceneAction';
	return {
		type,
		version: '1.0.0',
		domain: alarm ? 'security' : 'core',
		role: 'action',
		category: 'actions',
		label: alarm ? 'Alarm Action' : 'Scene Action',
		description: alarm
			? 'Trigger, enable, disable, or toggle an alarm'
			: 'Run or change the enabled state of another scene',
		icon: alarm ? 'fa-shield-halved' : 'fa-play-circle',
		color: alarm ? '#ef4444' : '#34d399',
		schema: {
			$schema: 'https://json-schema.org/draft/2020-12/schema',
			type: 'object',
			required: ['sceneId', 'actionType'],
			properties: {
				sceneId: { type: 'string', minLength: 1 },
				sceneName: { type: 'string' },
				filterType: { type: 'string', enum: [alarm ? 'alarm' : 'scene'] },
				actionType: { type: 'string', enum: actionOptions.map((option) => option.value) },
				playOption: { type: 'string', enum: playOptions.map((option) => option.value) },
				delay: {
					type: 'object',
					properties: {
						value: { type: 'number', minimum: 0 },
						units: { type: 'string', enum: ['sec', 'min'] },
					},
				},
			},
		},
		fields: [
			{
				path: 'sceneId',
				label: alarm ? 'Alarm' : 'Scene',
				type: 'resource',
				resource: {
					kind: 'scene',
					filter: alarm ? 'alarm' : 'scene',
					labelPath: 'sceneName',
				},
			},
			{ path: 'actionType', label: 'Action', type: 'select', options: actionOptions },
			{
				path: 'playOption',
				label: 'When running',
				type: 'select',
				options: playOptions,
				visibleWhen: { path: 'actionType', equals: 'play' },
				helperText: 'Choose whether the target checks its own conditions before running.',
			},
			{ path: 'delay.value', label: 'Delay', type: 'number', min: 0, step: 1, group: 'Timing' },
			{
				path: 'delay.units',
				label: 'Delay unit',
				type: 'select',
				group: 'Timing',
				options: [
					{ value: 'sec', label: 'Seconds' },
					{ value: 'min', label: 'Minutes' },
				],
			},
		],
		ports: { sources: ['output'] },
		defaults: () => ({
			sceneId: '',
			sceneName: '',
			filterType: alarm ? 'alarm' : 'scene',
			actionType: 'play',
			playOption: 'with_if_evaluation',
			delay: { value: 0, units: 'sec' },
			cardVersion: '1.0.0',
		}),
		summary: (data) => actionSummary(data, alarm),
		normalize: (data) => normalizeSceneAction(data, alarm),
		validate: (data) =>
			String(data.sceneId || '').trim()
				? []
				: [{ path: 'sceneId', message: `Select ${alarm ? 'an alarm' : 'a scene'}` }],
		compile: (node) => {
			if (node.data.sceneData) return { ...node.data.sceneData, _fid: node.id };
			const actionType = String(node.data.actionType || 'play');
			const option =
				actionType === 'play'
					? node.data.playOption === 'without_if_evaluation'
						? 'without_if_evaluation'
						: 'with_if_evaluation'
					: actionType === 'enable'
						? 'action_enable_scene'
						: actionType === 'disable'
							? 'action_disable_scene'
							: 'action_toggle_scene';
			const result = createSceneActionFromFlow({
				scene_id: String(node.data.sceneId || ''),
				option,
			} as any);
			return result
				? {
						...result,
						_fid: node.id,
						_flow_card: type,
						_flow_card_version: '1.0.0',
					}
				: null;
		},
		migrate: (data) => {
			const sceneData = data.sceneData as any;
			const sceneId = String(
				data.sceneId ??
					data.alarmId ??
					data.scene_id ??
					sceneData?.trigger ??
					sceneData?.r?.split(':')[1] ??
					'',
			);
			return normalizeSceneAction(
				{
					...data,
					sceneId,
					sceneName: data.sceneName ?? data.alarmName ?? '',
					actionType: data.actionType ?? 'play',
					playOption:
						data.playOption ??
						(sceneData?.skipif ? 'without_if_evaluation' : 'with_if_evaluation'),
					delay: data.delay ?? { value: 0, units: 'sec' },
				},
				alarm,
			);
		},
	};
}

function sceneConditionCard(alarm: boolean): FlowCardDefinition {
	const type = alarm ? 'alarmCondition' : 'sceneCondition';
	const noun = alarm ? 'Alarm' : 'Scene';
	return {
		type,
		version: '1.0.0',
		domain: alarm ? 'security' : 'core',
		role: 'condition',
		category: 'conditions',
		label: `${noun} State`,
		description: `Continue when the selected ${noun.toLowerCase()} is enabled or disabled`,
		icon: alarm ? 'fa-bell' : 'fa-diagram-project',
		color: alarm ? '#ef4444' : '#22d3ee',
		schema: {
			$schema: 'https://json-schema.org/draft/2020-12/schema',
			type: 'object',
			required: ['sceneId', 'state'],
			properties: {
				sceneId: { type: 'string', minLength: 1 },
				sceneName: { type: 'string' },
				filterType: { type: 'string', enum: [alarm ? 'alarm' : 'scene'] },
				state: { type: 'string', enum: ['enabled', 'disabled'] },
			},
		},
		fields: [
			{
				path: 'sceneId',
				label: noun,
				type: 'resource',
				resource: {
					kind: 'scene',
					filter: alarm ? 'alarm' : 'scene',
					labelPath: 'sceneName',
				},
			},
			{
				path: 'state',
				label: 'Required state',
				type: 'select',
				options: [
					{ value: 'enabled', label: 'Enabled' },
					{ value: 'disabled', label: 'Disabled' },
				],
			},
		],
		ports: { sources: ['true', 'false'] },
		defaults: () => ({
			sceneId: '',
			sceneName: '',
			filterType: alarm ? 'alarm' : 'scene',
			state: 'enabled',
			cardVersion: '1.0.0',
		}),
		summary: (data) =>
			data.sceneId
				? `${targetName(data, alarm)} is ${data.state === 'disabled' ? 'disabled' : 'enabled'}`
				: chooseTarget(alarm),
		normalize: (data) => ({
			...data,
			filterType: alarm ? 'alarm' : 'scene',
			label: data.sceneId
				? `${targetName(data, alarm)} is ${data.state === 'disabled' ? 'disabled' : 'enabled'}`
				: chooseTarget(alarm),
		}),
		validate: (data) =>
			String(data.sceneId || '').trim()
				? []
				: [{ path: 'sceneId', message: `Select ${alarm ? 'an alarm' : 'a scene'}` }],
		compile: (node) => {
			if (node.data.sceneData) return { ...node.data.sceneData, _fid: node.id };
			return {
				r: `blprop:${String(node.data.sceneId || '')}:enabled`,
				cmpop: 'eq',
				cmpval: node.data.state === 'disabled' ? '0' : '1',
				_gui_purpose: 'condition',
				_fid: node.id,
				_flow_card: type,
				_flow_card_version: '1.0.0',
			};
		},
		migrate: (data) => ({
			...data,
			sceneId: String(data.sceneId ?? data.alarmId ?? data.scene_id ?? ''),
			sceneName: data.sceneName ?? data.alarmName ?? '',
			filterType: alarm ? 'alarm' : 'scene',
			state: data.state ?? 'enabled',
		}),
	};
}

export const sceneResourceCards = [
	sceneConditionCard(false),
	sceneConditionCard(true),
	sceneActionCard(false),
	sceneActionCard(true),
];
